// Icon set for the app. Generic UI glyphs come from lucide-react; the sport marks
// are hand-rolled to match the design export. All inherit `currentColor`, so colour
// and stroke follow the surrounding text colour.
import type { SVGProps } from 'react';

export {
  Bell,
  Megaphone,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Lock,
  Trophy,
  Users,
  User,
  UserPlus,
  LayoutGrid,
  Info,
  Pencil,
  Share,
  Share2,
  Check,
  X,
  Zap,
  Minus,
  GripVertical,
  Trash2,
  Crown,
  Star,
  Medal,
  Mountain,
  Palette,
  Copy,
  MoreHorizontal,
  MessageCircle,
  LifeBuoy,
  Flag,
  Ban,
  LogOut,
  Eye,
  EyeOff,
  Camera,
  Mail,
  Swords,
  Flame,
  Target,
  Clock,
  AlertTriangle,
  Calendar,
  Lightbulb,
  Plus,
  Settings,
  TrendingUp,
  ListOrdered,
  FileText
} from 'lucide-react';

type IconProps = SVGProps<SVGSVGElement>;

/** Apple wordmark glyph for the "Continue with Apple" button (solid, inherits currentColor). */
export function AppleLogo(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M16.36 12.78c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.82-.81-3-.79-1.54.02-2.96.9-3.75 2.28-1.6 2.78-.41 6.89 1.15 9.14.76 1.1 1.67 2.34 2.86 2.29 1.15-.05 1.58-.74 2.97-.74 1.39 0 1.78.74 3 .72 1.24-.02 2.02-1.12 2.78-2.23.88-1.28 1.24-2.52 1.26-2.58-.03-.01-2.41-.93-2.43-3.69zM14.13 6.04c.64-.77 1.07-1.85.95-2.92-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.78-.97 2.83 1.02.08 2.07-.52 2.71-1.29z" />
    </svg>
  );
}

/** Google "G" mark (full colour) for the "Continue with Google" button. */
export function GoogleLogo(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <path
        fill="#4285F4"
        d="M21.6 12.23c0-.68-.06-1.34-.18-1.96H12v3.71h5.38a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.89-1.74 2.99-4.3 2.99-7.27z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.23-2.5c-.9.6-2.05.95-3.39.95-2.6 0-4.81-1.76-5.6-4.12H3.06v2.59A10 10 0 0 0 12 22z"
      />
      <path
        fill="#FBBC05"
        d="M6.4 13.9a6 6 0 0 1 0-3.8V7.51H3.06a10 10 0 0 0 0 8.98l3.34-2.59z"
      />
      <path
        fill="#EA4335"
        d="M12 5.98c1.47 0 2.79.5 3.83 1.5l2.86-2.86A10 10 0 0 0 12 2a10 10 0 0 0-8.94 5.51l3.34 2.59C7.19 7.74 9.4 5.98 12 5.98z"
      />
    </svg>
  );
}

const base = (props: IconProps) => ({
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  ...props
});

export function SoccerBall(props: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.5, ...props })}>
      <circle cx="12" cy="12" r="9.2" />
      <path d="M12 7.4l3.4 2.5-1.3 4h-4.2l-1.3-4z" fill="currentColor" />
      <path d="M12 2.8v4.6M4.4 9.9l3.8 1.3M19.6 9.9l-3.8 1.3M7.2 20.4l2.2-3.5M16.8 20.4l-2.2-3.5" />
    </svg>
  );
}

export function Basketball(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3v18M5.3 5.3c3.7 3.5 9.7 3.5 13.4 0M5.3 18.7c3.7-3.5 9.7-3.5 13.4 0" />
    </svg>
  );
}

export function Tennis(props: IconProps) {
  return (
    <svg {...base(props)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M5 6c3.2 3 3.2 9 0 12M19 6c-3.2 3-3.2 9 0 12" />
    </svg>
  );
}

export function AmFootball(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 12c0-4 4-8 8-8s8 4 8 8-4 8-8 8-8-4-8-8z" transform="rotate(-35 12 12)" />
      <path d="M9.5 12h5M11 10.3v3.4M13 10.3v3.4" />
    </svg>
  );
}

export function Cricket(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M14.5 4.5l5 5-9 9-5-5z" />
      <path d="M6.5 13.5l4 4" />
      <circle cx="18" cy="6" r="2.4" />
    </svg>
  );
}
